import { CheckCircle2, ChevronDown, ChevronRight, Loader2, Search } from "lucide-react";
import { useState } from "react";

import type { StoreDto } from "../../shared/types";
import { proxiedImageUrl } from "../lib/imageProxy";
import { Button, ErrorState, stateBorderClass, StateLabel } from "../ui";
import { Modal } from "./Modal";
import { PriceTag } from "./PriceTag";
import { StockBadge } from "./StockBadge";

type DetectedSampleProduct = {
	title: string;
	url: string;
	priceMinor: number | null;
	imageUrl: string | null;
	inStock: boolean;
};

type DetectedCategory = {
	key: string;
	name: string;
	productCount: number | null;
};

type DetectionResult = {
	ok: boolean;
	stack: "woocommerce" | "shopify" | "magento+html" | "prestashop" | null;
	baseUrl: string;
	suggestedId: string;
	suggestedName: string;
	totalItems: number | null;
	totalItemsApprox: boolean;
	sampleProducts: DetectedSampleProduct[];
	categories: DetectedCategory[];
	errors: string[];
};

const STACK_LABELS: Record<string, string> = {
	woocommerce: "WooCommerce",
	shopify: "Shopify",
	"magento+html": "Magento (GraphQL)",
	prestashop: "PrestaShop",
};

type Phase = "idle" | "detecting" | "detected" | "saving" | "error";

type Props = {
	open: boolean;
	onClose: () => void;
	onCreated: (store: StoreDto) => void;
};

export function AddStoreModal({ open, onClose, onCreated }: Props) {
	const [url, setUrl] = useState("");
	const [phase, setPhase] = useState<Phase>("idle");
	const [error, setError] = useState<string | null>(null);
	const [detection, setDetection] = useState<DetectionResult | null>(null);
	const [storeId, setStoreId] = useState("");
	const [storeName, setStoreName] = useState("");
	const [categoriesOpen, setCategoriesOpen] = useState(false);

	const reset = () => {
		setUrl("");
		setPhase("idle");
		setError(null);
		setDetection(null);
		setStoreId("");
		setStoreName("");
		setCategoriesOpen(false);
	};

	const handleClose = () => {
		if (phase === "saving") return;
		reset();
		onClose();
	};

	const handleDetect = async () => {
		const trimmed = url.trim();
		if (!trimmed) return;
		setPhase("detecting");
		setError(null);
		setDetection(null);
		try {
			const res = await fetch("/api/stores/detect", {
				method: "POST",
				headers: { "content-type": "application/json" },
				body: JSON.stringify({ url: trimmed }),
			});
			const data = (await res.json()) as DetectionResult & { error?: string };
			if (!res.ok || data.error) {
				setPhase("error");
				setError(data.error ?? `HTTP ${res.status}`);
				return;
			}
			setDetection(data);
			setStoreId(data.suggestedId);
			setStoreName(data.suggestedName);
			setPhase(data.ok ? "detected" : "error");
			if (!data.ok) {
				setError(data.errors[0] ?? "no supported backend detected at this URL");
			}
		} catch (e) {
			setPhase("error");
			setError((e as Error).message);
		}
	};

	const handleSave = async () => {
		if (!detection || !detection.ok || !detection.stack) return;
		if (!/^[a-z][a-z0-9-]{1,30}$/.test(storeId)) {
			setError("id must start with a letter and contain only lowercase letters, digits, and dashes (2-31 chars)");
			return;
		}
		if (!storeName.trim()) {
			setError("name is required");
			return;
		}
		setPhase("saving");
		setError(null);
		try {
			const res = await fetch("/api/stores", {
				method: "POST",
				headers: { "content-type": "application/json" },
				body: JSON.stringify({
					id: storeId,
					name: storeName.trim(),
					baseUrl: detection.baseUrl,
					enabled: true,
					config: { stack: detection.stack },
				}),
			});
			const data = (await res.json()) as { store: StoreDto } | { error: string };
			if (!res.ok || "error" in data) {
				setPhase("detected");
				setError("error" in data ? data.error : `HTTP ${res.status}`);
				return;
			}
			onCreated(data.store);
			reset();
		} catch (e) {
			setPhase("detected");
			setError((e as Error).message);
		}
	};

	const stackLabel = detection?.stack != null ? (STACK_LABELS[detection.stack] ?? detection.stack) : null;
	const detected = phase === "detected" && detection?.ok === true;
	const canSave = detected;
	const idValid = storeId === "" || /^[a-z][a-z0-9-]{1,30}$/.test(storeId);

	return (
		<Modal
			open={open}
			onClose={handleClose}
			title="Add store"
			subtitle="Detect a store's backend by URL and add it to the catalog"
			size="lg"
			footer={
				<>
					<Button type="button" variant="secondary" size="sm" onClick={handleClose} disabled={phase === "saving"}>
						Cancel
					</Button>
					<Button
						type="button"
						variant="primary"
						size="sm"
						onClick={handleSave}
						disabled={!canSave || !idValid || !storeName.trim()}
					>
						{phase === "saving" ? (
							<Loader2 className="size-3.5 animate-spin" aria-hidden />
						) : (
							<CheckCircle2 className="size-3.5" aria-hidden />
						)}
						Add store
					</Button>
				</>
			}
		>
			<div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-3">
				<div>
					<label className="mb-1 block text-xs font-medium text-muted">Store URL</label>
					<div className="flex gap-2">
						<input
							type="url"
							value={url}
							onChange={(e) => setUrl(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter" && phase !== "detecting" && phase !== "saving") {
									e.preventDefault();
									void handleDetect();
								}
							}}
							placeholder="https://example.no"
							disabled={phase === "detecting" || phase === "saving"}
							className="min-h-tap min-w-0 flex-1 rounded-md border border-hairline bg-ground px-2.5 text-[13px] text-ink outline-none placeholder:text-muted focus:border-line disabled:opacity-50"
						/>
						<Button
							type="button"
							variant="secondary"
							onClick={handleDetect}
							disabled={!url.trim() || phase === "detecting" || phase === "saving"}
						>
							{phase === "detecting" ? (
								<Loader2 className="size-3.5 animate-spin" aria-hidden />
							) : (
								<Search className="size-3.5" aria-hidden />
							)}
							Detect
						</Button>
					</div>
					{phase === "detecting" && (
						<p className="mt-2 text-xs text-muted">Probing WooCommerce, Shopify, and Magento endpoints…</p>
					)}
				</div>

				{error && <ErrorState message={error} />}

				{detected && detection && (
					<>
						<div className="flex items-center gap-2 rounded-md border border-hairline bg-ground px-2.5 py-1.5 text-xs text-ink">
							<CheckCircle2 className="size-3.5 shrink-0" aria-hidden />
							<span>
								<strong className="font-semibold">{stackLabel}</strong> detected
								{detection.totalItems != null
									? ` · ${detection.totalItemsApprox ? "≥" : ""}${detection.totalItems.toLocaleString()} items`
									: ""}
							</span>
						</div>

						<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
							<div>
								<label className="mb-1 block text-xs font-medium text-muted">ID</label>
								<input
									type="text"
									value={storeId}
									onChange={(e) => setStoreId(e.target.value.toLowerCase().trim())}
									className={`min-h-tap w-full rounded-md border bg-ground px-2.5 font-mono text-[13px] text-ink outline-none focus:border-line ${idValid ? "border-hairline" : stateBorderClass("fault")}`}
								/>
								{!idValid && <StateLabel state="fault" label="Invalid id" />}
								<p className="mt-1 text-[11px] text-muted">
									lowercase letters, digits, dashes; must start with a letter
								</p>
							</div>
							<div>
								<label className="mb-1 block text-xs font-medium text-muted">Name</label>
								<input
									type="text"
									value={storeName}
									onChange={(e) => setStoreName(e.target.value)}
									className="min-h-tap w-full rounded-md border border-hairline bg-ground px-2.5 text-[13px] text-ink outline-none focus:border-line"
								/>
							</div>
						</div>

						<div>
							<label className="mb-1 block text-xs font-medium text-muted">Base URL</label>
							<p className="rounded-md border border-hairline bg-ground px-2.5 py-1.5 font-mono text-xs break-all text-ink">
								{detection.baseUrl}
							</p>
						</div>

						{detection.categories.length > 0 && (
							<div>
								<button
									type="button"
									onClick={() => setCategoriesOpen((v) => !v)}
									className="flex min-h-tap items-center gap-1 text-xs font-medium text-ink hover:underline"
								>
									{categoriesOpen ? (
										<ChevronDown className="size-3.5" aria-hidden />
									) : (
										<ChevronRight className="size-3.5" aria-hidden />
									)}
									Categories ({detection.categories.length})
								</button>
								{categoriesOpen && (
									<ul className="mt-1 max-h-48 space-y-0.5 overflow-y-auto rounded-md border border-hairline bg-ground px-2.5 py-1.5 text-xs">
										{detection.categories.map((c) => (
											<li key={c.key} className="flex justify-between gap-2 text-ink">
												<span className="truncate">{c.name}</span>
												{c.productCount != null && (
													<span className="shrink-0 text-muted">{c.productCount.toLocaleString()}</span>
												)}
											</li>
										))}
									</ul>
								)}
							</div>
						)}

						{detection.sampleProducts.length > 0 && (
							<div>
								<p className="mb-1 text-xs font-medium text-muted">Sample products</p>
								<ul className="grid gap-1.5 sm:grid-cols-2">
									{detection.sampleProducts.map((p, i) => (
										<li
											key={`${p.url}-${i}`}
											className="flex items-center gap-2.5 rounded-md border border-hairline bg-ground px-2 py-1.5"
										>
											{p.imageUrl ? (
												<img
													src={proxiedImageUrl(p.imageUrl) ?? undefined}
													alt=""
													className="size-10 shrink-0 rounded object-cover"
													loading="lazy"
												/>
											) : (
												<div className="size-10 shrink-0 rounded bg-paper" />
											)}
											<div className="min-w-0 flex-1">
												<p className="truncate text-xs text-ink">{p.title || "(no title)"}</p>
												<StockBadge inStock={p.inStock} />
											</div>
											{p.priceMinor != null && <PriceTag minor={p.priceMinor} />}
										</li>
									))}
								</ul>
							</div>
						)}
					</>
				)}
			</div>
		</Modal>
	);
}
